// Direction B "Ink Header" composer.
//   - Container: 12px×14px×10px padding, 1px top border (border-muted), white bg.
//   - Input: 38px tall, pill (9999px), 1px border, focus = ink border + 3px
//     soft-pink ring.
//   - Send: 38×38 ink circle. White hairline send-arrow. Hover muted-800.
//
// Per spec sec 1.5 + 3.4 (Pattern A) — preserved from prior impl:
//   - Send button DISABLED while status is 'submitted' or 'error' (intentional friction).
//   - Button stays CLICKABLE during 'streaming' so the user can click it to invoke onStop.
//   - Textarea remains enabled during stream — user can type the next thought,
//     just can't submit yet.
//   - Enter during stream = no-op (forces an intentional click on the now-stop
//     button to cancel, then a separate Enter to send).
//   - Icon flips from send to stop during stream (data-icon attribute drives
//     both visual and tests).
//
// Direction B note: the in-conversation send button is INK (#0a0a0a), not
// brand pink. Pink is reserved for the welcome CTA conversion moment — using
// pink here would dilute that signal.

import { useState, KeyboardEvent } from 'react'
import { SEND_SVG, STOP_SVG } from '../icons/icons'

export interface InputBarProps {
  status: 'ready' | 'submitted' | 'streaming' | 'error'
  onSend: (text: string) => void
  onStop: () => void
}

export function InputBar({ status, onSend, onStop }: InputBarProps) {
  const [text, setText] = useState('')
  const isReady = status === 'ready'
  const isStreaming = status === 'streaming'
  const isInteractive = isReady || isStreaming

  const handleSubmit = () => {
    if (!isReady) return
    const trimmed = text.trim()
    if (!trimmed) return
    onSend(trimmed)
    setText('')
  }

  const handleKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (isStreaming) return
      handleSubmit()
    }
  }

  const handleButton = () => {
    if (isStreaming) onStop()
    else handleSubmit()
  }

  return (
    <div className="flex-shrink-0 border-t border-border-muted bg-paper px-3.5 pt-3 pb-2.5 flex items-center gap-2">
      <textarea
        data-testid="input-textarea"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKey}
        placeholder="Type a message…"
        rows={1}
        aria-label="Message Mikla"
        className={[
          'flex-1 h-[38px] resize-none px-3.5 py-[9px] rounded-full',
          'border border-border bg-paper',
          'font-sans text-sm text-ink leading-5',
          'placeholder:text-muted-400',
          'outline-none transition-colors',
          'focus:border-ink focus:ring-[3px] focus:ring-accent/10',
        ].join(' ')}
      />
      <button
        type="button"
        data-testid="send-button"
        data-icon={isStreaming ? 'stop' : 'send'}
        onClick={handleButton}
        disabled={!isInteractive}
        aria-label={isStreaming ? 'Stop' : 'Send'}
        className={[
          'w-[38px] h-[38px] rounded-full flex-shrink-0',
          'bg-ink text-white hover:bg-muted-800',
          'flex items-center justify-center',
          'transition-colors',
          'disabled:opacity-50 disabled:cursor-not-allowed',
        ].join(' ')}
      >
        <span
          dangerouslySetInnerHTML={{ __html: isStreaming ? STOP_SVG : SEND_SVG }}
          className={isStreaming ? 'w-3.5 h-3.5 inline-block' : 'w-3.5 h-3.5 inline-block'}
        />
      </button>
    </div>
  )
}
